import {
  faInfoCircle,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import React, { useMemo,useState } from 'react';
import Loader from 'src/components/loader';
import Alert from 'src/components/ui/Alert';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { getShortUuid } from 'src/utils/url';

const INITIAL_VISIBLE_COUNT = 3;

const ScrapModal = ({
  onConfirm,
  onDismiss,
  onCancelAndRemanufacture,
  selectedPieces,
}) => {
  const [scrapNotes, setScrapNotes] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showAllPieces, setShowAllPieces] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { closeActionPanel } = useActionPanelStore();

  const isScrapReasonValid = scrapNotes.trim().length > 0;
  const showScrapError = submitAttempted && !isScrapReasonValid;

  const visiblePieces = useMemo(() => {
    if (showAllPieces) {
      return selectedPieces;
    }

    return selectedPieces.slice(0, INITIAL_VISIBLE_COUNT);
  }, [selectedPieces, showAllPieces]);

  const remainingCount =
    selectedPieces.length - INITIAL_VISIBLE_COUNT;

  const pieceBadges = (
    <div className="d-flex flex-wrap gap-5 align-items-center">
      {visiblePieces.map((piece) => (
        <span
          key={piece}
          className="status-badge status-badge--error"
        >
          {getShortUuid(piece)}
        </span>
      ))}

      {remainingCount > 0 && (
        <button
          type="button"
          className="status-badge border-0 bg-transparent status-badge--error text-capitalize"
          onClick={() => setShowAllPieces((prev) => !prev)}
        >
          {showAllPieces
            ? 'Show less'
            : `+ ${remainingCount} more`}
        </button>
      )}
    </div>
  );

  return (
    <div>
      <div className="mb-3">
        {pieceBadges}
      </div>
      <Alert
        variant="warning"
        name="scrap-warning"
        icon={faTriangleExclamation}
        iconClassName="mt-1"
        className="d-flex align-items-start gap-10"
      >
        Scrapping will automatically end the workflow for the selected pieces.
        They will be marked as incomplete. This cannot be undone.
      </Alert>

      <Alert
        variant="info"
        name="scrap-info"
        icon={faInfoCircle}
        iconClassName="mt-1"
        className="d-flex align-items-start gap-10"
      >
        If you want to remake these pieces, tap{' '}
        <button
          type="button"
          className="btn btn-link p-0 fw-semibold align-baseline"
          onClick={onCancelAndRemanufacture}
        >
          Cancel
        </button>{' '}
        and select Remanufacture instead.
      </Alert>

      <div className="mb-4">
        <label>
          Reason
          <span className="required-text fw-bold">*</span>
        </label>

        <textarea
          className="form-control"
          value={scrapNotes}
          onChange={(event) => setScrapNotes(event.target.value)}
        />

        {showScrapError && (
          <span className="required-text small">
            A reason is required before confirming scrap.
          </span>
        )}
      </div>
      <div className='button-group button-group--panel'>
        <button
          type="button"
          className="button"
          disabled={isSubmitting}
          onClick={() => {
            onDismiss();
            closeActionPanel();
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={isSubmitting}
          onClick={async () => {
            setSubmitAttempted(true);
            if (!scrapNotes.trim()) return;

            try {
              setIsSubmitting(true);
              await onConfirm(scrapNotes);
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          {!isSubmitting ? 'Confirm Scrap' : <Loader inline />}
        </button>
      </div>
    </div>
  );
};

ScrapModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onCancelAndRemanufacture: PropTypes.func.isRequired,
  selectedPieces: PropTypes.array.isRequired,
};

export default ScrapModal;
